import { Box, Container, Flex, FocusLock, IconButton, Portal } from "@chakra-ui/react";
import BundleIcon from "./ui/BundleIcon";
import { useEffect, type ReactNode } from "react";
import { useI18n } from "next-localization";
import { pxToRem } from "themes/utils/mapPxToRem";

interface ModalOverlayProps {
    closeOverlay: () => void;
    children: ReactNode;
    prev: JSX.Element;
    next: JSX.Element;
    modalRef?: React.RefObject<HTMLDivElement>;
}

export const ModalPrevArrow = () => {
    return (
        <BundleIcon
            name="ArrowForward"
            width={[pxToRem("32px"), null, pxToRem("45px")]}
            height={[pxToRem("45px"), null, pxToRem("90px")]}
            color={"black"}
            style={{ transform: "scaleX(-1)" }}
        />
    );
};

export const ModalNextArrow = () => {
    return (
        <BundleIcon
            name="ArrowForward"
            width={[pxToRem("32px"), null, pxToRem("45px")]}
            height={[pxToRem("45px"), null, pxToRem("90px")]}
            color={"black"}
        />
    );
};

export const ModalOverlay = ({
    closeOverlay,
    children,
    prev,
    next,
    modalRef,
}: ModalOverlayProps) => {
    const { t } = useI18n();
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeOverlay();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [closeOverlay]);

    // Lock scroll if partner overlay is open
    useEffect(() => {
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    return (
        <Portal>
            <FocusLock autoFocus restoreFocus>
                <Box
                    position="fixed"
                    top={0}
                    bottom={0}
                    left={0}
                    right={0}
                    zIndex={200}
                    bg={["white", null, "rgba(0, 0, 0, 0.7)"]}
                    padding={["sp16", null, null, "sp32"]}
                    paddingTop={["sp80", null, null, "sp32"]}
                    maxH="100svh"
                    overflow={["auto", null, "hidden"]}
                    ref={modalRef}
                >
                    <Container
                        position="relative"
                        bg="white"
                        padding={0}
                        minHeight={["100%", null, "auto"]}
                        maxH={[null, null, null, "calc(100svh - 64px)"]}
                        overflow="auto"
                    >
                        <Flex
                            flexDir={["column", null, "row"]}
                            alignItems="center"
                            justifyContent={["center", null, "space-between"]}
                            gap={["sp16", null, "sp40"]}
                            p={[null, null, "sp32"]}
                            py={["sp16", null, "sp80"]}
                            pb={["sp72", null, "sp80"]}
                        >
                            <IconButton
                                position={["fixed", null, "absolute"]}
                                right={pxToRem("24px")}
                                top={pxToRem("24px")}
                                w={pxToRem("40px")}
                                h={pxToRem("40px")}
                                variant="outline"
                                bgColor="white"
                                borderRadius={"50%"}
                                onClick={closeOverlay}
                                icon={<BundleIcon name="Close" />}
                                aria-label={t("closeModal")}
                            />
                            <Box as="span" display={["none", null, "block"]}>
                                {prev}
                            </Box>

                            {children}

                            <Box as="span" display={["none", null, "block"]}>
                                {next}
                            </Box>
                        </Flex>
                    </Container>
                    <Flex
                        display={["flex", null, "none"]}
                        justify="space-between"
                        alignItems="center"
                        paddingX={[0, null, "sp24"]}
                        w={"full"}
                        bgColor={"white"}
                        position={"fixed"}
                        p={"sp16"}
                        bottom={0}
                        left={0}
                    >
                        {prev}
                        {next}
                    </Flex>
                </Box>
            </FocusLock>
        </Portal>
    );
};
